import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import { aiExperiencePromptActionPluginKey } from '../../pm-plugins/prompt-action/prompt-action-plugin-key';
import { ACTIONS } from '../../pm-plugins/prompt-action/states';
import type { AIGlobalOptIn } from '../../types/types';
import { improveWriting } from '../prebuilt/config-items/prompts/enhance/enhance';

const MIN_PASTE_CONTENT_SIZE = 200;
const MAX_PASTE_CONTENT_SIZE = 1200;

export const shouldTriggerOnPaste = (text: string | undefined): boolean => {
	// Only check text character count
	if (!text) {
		return false;
	}

	const charCount = text.length;

	return (
		charCount >= MIN_PASTE_CONTENT_SIZE &&
		charCount <= MAX_PASTE_CONTENT_SIZE &&
		expValEquals('cc_improve_writing_on_paste_v2', 'isEnabled', true)
	);
};

export const handlePasteStateChange = ({
	pasteState,
	editorView,
	aiOptIn,
	locale,
	pasteData,
	setImproveWritingPasteData,
}: {
	aiOptIn: AIGlobalOptIn;
	editorView: EditorView | undefined;
	locale: string;
	pasteData: {
		firstPasteViewed: boolean | undefined;
		secondPasteViewed: boolean | undefined;
	};
	pasteState:
		| { lastContentPasted?: { pasteEndPos: number; pasteStartPos: number; text: string } | null }
		| undefined;
	setImproveWritingPasteData: ({
		firstPasteViewed,
		secondPasteViewed,
	}: {
		firstPasteViewed?: boolean;
		secondPasteViewed?: boolean;
	}) => void;
}) => {
	if (
		!pasteState?.lastContentPasted ||
		pasteData.firstPasteViewed === undefined ||
		pasteData.secondPasteViewed === undefined
	) {
		// is pasteData is undefined (possibly from loading or an error) prevent AI modal or flag from displaying
		return;
	}

	const { lastContentPasted } = pasteState;

	// Check if the pasted content is eligible
	const shouldTrigger = shouldTriggerOnPaste(lastContentPasted.text);

	if (
		shouldTrigger &&
		editorView &&
		lastContentPasted.pasteStartPos !== undefined &&
		lastContentPasted.pasteEndPos !== undefined
	) {
		setImproveWritingPasteData({ firstPasteViewed: true });
		if (!pasteData.firstPasteViewed) {
			const tr = editorView.state.tr.setSelection(
				TextSelection.create(
					editorView.state.doc,
					lastContentPasted.pasteStartPos,
					lastContentPasted.pasteEndPos,
				),
			);
			editorView.dispatch(tr);

			// Create and execute the AI modal command for Improve Writing
			const openAIModalCommand = createOpenAIModalCommand({
				state: editorView.state,
				configItem: improveWriting(),
				lastTriggeredFrom: 'auto_paste',
				aiGlobalOptIn: aiOptIn,
				locale,
			});

			openAIModalCommand(editorView.state, editorView.dispatch);
		} else if (!pasteData.secondPasteViewed) {
			setImproveWritingPasteData({ secondPasteViewed: true });

			const tr = editorView.state.tr;
			tr.setMeta(aiExperiencePromptActionPluginKey, {
				type: ACTIONS.ACTION_COMPLETED,
				data: {
					actionSource: 'auto-paste',
					actionType: 'second-paste',
					prompt: 'improve-writing',
				},
			});
			editorView.dispatch(tr);
		}
	}
};
